import { useEffect, useState } from "react";
import { Camera, LockKeyhole, ShieldCheck, Truck } from "lucide-react";
import { nextBenefitIndex, shouldRotateBenefits, siteBenefits, BENEFIT_DISPLAY_MS, BENEFIT_FADE_MS } from "./data/benefits";
import type { PublicConfig } from "./lib/api";

const icons = { payments: LockKeyhole, protection: ShieldCheck, shipping: Truck, photography: Camera };

export default function BenefitTicker({ config, onExplore }: { config: PublicConfig | null; onExplore: () => void }) {
  const benefits = siteBenefits(config);
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [manuallyStopped, setManuallyStopped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  const [reducedMotion, setReducedMotion] = useState(() => matchMedia("(prefers-reduced-motion: reduce)").matches);
  const rotating = shouldRotateBenefits({ manuallyStopped, hovered, focused, visible, reducedMotion });
  const benefit = benefits[index];
  const Icon = icons[benefit.id];
  useEffect(() => {
    const preference = matchMedia("(prefers-reduced-motion: reduce)");
    const updateVisibility = () => { setVisible(document.visibilityState === "visible"); setLeaving(false); };
    const updatePreference = () => { setReducedMotion(preference.matches); setLeaving(false); };
    document.addEventListener("visibilitychange", updateVisibility);
    preference.addEventListener("change", updatePreference);
    return () => { document.removeEventListener("visibilitychange", updateVisibility); preference.removeEventListener("change", updatePreference); };
  }, []);
  useEffect(() => {
    if (!rotating) return;
    const timer = window.setTimeout(() => {
      if (leaving) { setIndex((current) => nextBenefitIndex(current, benefits.length)); setLeaving(false); }
      else setLeaving(true);
    }, leaving ? BENEFIT_FADE_MS : BENEFIT_DISPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [index, leaving, rotating, benefits.length]);
  const description = `${benefit.label}${benefit.comingSoon ? ", in arrivo" : ""}. Scopri come funziona GRUVO.`;
  return <div className="benefit-bar"><div className="container benefit-bar-inner">
    <button className="benefit-ticker" aria-label={description} aria-live="off" aria-describedby="benefit-rotation-instructions"
      onMouseEnter={() => { setHovered(true); setLeaving(false); }} onMouseLeave={() => setHovered(false)}
      onFocus={() => { setFocused(true); setLeaving(false); }} onBlur={() => setFocused(false)}
      onPointerDown={() => { setManuallyStopped(true); setLeaving(false); }} onClick={onExplore}
      onKeyDown={(event) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault(); setManuallyStopped(true); setLeaving(false);
        setIndex((current) => nextBenefitIndex(current, benefits.length, event.key === "ArrowLeft" ? -1 : 1));
      }}>
      <span key={benefit.id} className={`benefit-message${leaving && rotating ? " is-leaving" : ""}`} aria-hidden="true">
        <Icon size={13} strokeWidth={1.8} /><span>{benefit.label}</span>{benefit.comingSoon && <span className="benefit-status">in arrivo</span>}
      </span>
    </button>
    <span id="benefit-rotation-instructions" className="benefit-instructions">Un vantaggio alla volta, con alternanza automatica. Il focus e il passaggio del puntatore la fermano. Usa le frecce sinistra e destra per scegliere un messaggio e mantenerlo.</span>
  </div></div>;
}
